import { useState } from 'react'
import { Button, Input, message, Select, Space, Table, Tag, Typography } from 'antd'
import { copyText, sqlExplainAI, sqlFormat } from '@/api/logText'
import type { SqlExplainAIResult } from '@/types'

const { TextArea } = Input
const { Text } = Typography
const MONO = { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }

const DIALECTS = ['MySQL', 'PostgreSQL', 'Oracle', 'SQL Server', 'SQLite'].map((v) => ({ value: v, label: v }))

/** SQL 与 EXPLAIN 解读页签：本地格式化 + AI 执行计划解读 */
export default function SqlExplainTab() {
  const [sql, setSql] = useState('')
  const [formatted, setFormatted] = useState('')
  const [plan, setPlan] = useState('')
  const [dialect, setDialect] = useState('MySQL')
  const [formatting, setFormatting] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiResult, setAiResult] = useState<SqlExplainAIResult | null>(null)

  /** 本地格式化 */
  const handleFormat = async () => {
    if (!sql.trim()) {
      message.warning('请输入 SQL')
      return
    }
    setFormatting(true)
    try {
      const res = await sqlFormat({ sql })
      setFormatted(res.formatted)
      message.success(`格式化完成，共 ${res.line_count} 行`)
    } finally {
      setFormatting(false)
    }
  }

  /** AI 解读执行计划 */
  const handleExplain = async () => {
    if (!sql.trim()) {
      message.warning('请输入 SQL')
      return
    }
    setAiLoading(true)
    try {
      setAiResult(await sqlExplainAI({ sql: formatted || sql, plan, dialect }))
    } finally {
      setAiLoading(false)
    }
  }

  const riskColumns = [
    { title: '表名', dataIndex: 'table', width: 180 },
    { title: '风险', dataIndex: 'issue', ellipsis: true },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 14, padding: '0 22px 12px', overflowY: 'auto' }}>
      {/* ===== SQL 与格式化 ===== */}
      <div style={{ flexShrink: 0 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>SQL 与格式化</Text>
        <TextArea
          rows={5}
          placeholder="粘贴 SQL 语句"
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          style={MONO}
        />
        <Space wrap style={{ marginTop: 8 }}>
          <Button loading={formatting} onClick={handleFormat}>格式化</Button>
          {formatted && <Button type="link" style={{ padding: 0 }} onClick={() => copyText(formatted)}>复制格式化结果</Button>}
        </Space>
        {formatted && (
          <pre style={{ ...MONO, whiteSpace: 'pre-wrap', background: 'var(--au-surface, #fafafa)', padding: 10, borderRadius: 8, marginTop: 8, maxHeight: 260, overflowY: 'auto' }}>
            {formatted}
          </pre>
        )}
      </div>

      {/* ===== EXPLAIN AI 解读 ===== */}
      <div style={{ flexShrink: 0 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>执行计划 AI 解读（复用 AI 配置中的模型）</Text>
        <TextArea
          rows={4}
          placeholder="粘贴 EXPLAIN / EXPLAIN ANALYZE 输出（可空，为空则仅解读 SQL）"
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          style={MONO}
        />
        <Space wrap style={{ marginTop: 8 }}>
          <Select value={dialect} onChange={setDialect} options={DIALECTS} style={{ width: 140 }} />
          <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }} loading={aiLoading} onClick={handleExplain}>
            AI 解读
          </Button>
        </Space>
        {aiResult && !aiResult.parsed && (
          <div style={{ marginTop: 8 }}>
            <Tag color="warning">AI 返回格式未解析，原文：</Tag>
            <pre style={{ ...MONO, whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{aiResult.raw}</pre>
          </div>
        )}
        {aiResult?.parsed && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {aiResult.sql_summary && (
              <div>
                <Text strong>SQL 作用：</Text>
                <Text> {aiResult.sql_summary}</Text>
              </div>
            )}
            {aiResult.plan_analysis && (
              <div>
                <Text strong>执行计划解读：</Text>
                <div><Text type="secondary">{aiResult.plan_analysis}</Text></div>
              </div>
            )}
            {(aiResult.risk_tables ?? []).length > 0 && (
              <div>
                <Text strong>风险表：</Text>
                <Table
                  size="small"
                  style={{ marginTop: 6 }}
                  columns={riskColumns}
                  dataSource={aiResult.risk_tables}
                  rowKey={(_, i) => String(i)}
                  pagination={false}
                />
              </div>
            )}
            {(aiResult.optimizations ?? []).length > 0 && (
              <div>
                <Text strong>优化建议：</Text>
                {aiResult.optimizations!.map((o, i) => (
                  <div key={i}><Text>· {o}</Text></div>
                ))}
              </div>
            )}
            {(aiResult.index_suggestions ?? []).length > 0 && (
              <div>
                <Text strong>索引建议：</Text>
                <Space wrap style={{ marginTop: 4 }}>
                  {aiResult.index_suggestions!.map((o, i) => <Tag key={i} color="cyan">{o}</Tag>)}
                </Space>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
